import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, FileText, User, Clock, AlertCircle, CheckCircle, XCircle, Edit, Trash2, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { useAuditLogs, fetchAllAuditLogs, type AuditLogEntry } from '@/api-services/hooks/useAuditLogs';

interface AuditLog {
  id: number;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | string;
  entity: string;
  entityId: string;
  performedBy: string;
  performedByRole: string;
  timestamp: string;
  ipAddress: string;
  details: string;
  status: 'SUCCESS' | 'FAILURE';
}

// Map API response to AuditLog interface
const mapApiLogToAuditLog = (apiLog: AuditLogEntry): AuditLog => {
  return {
    id: apiLog.id,
    action: apiLog.action,
    entity: apiLog.entity,
    entityId: apiLog.entityId.toString(),
    performedBy: apiLog.createdBy,
    performedByRole: '', // API doesn't provide role, leaving empty
    timestamp: apiLog.createdDate,
    ipAddress: apiLog.createdSystemIp,
    details: apiLog.logMessage,
    status: apiLog.status as 'SUCCESS' | 'FAILURE',
  };
};

const AuditLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Fetch audit logs from API — server-side pagination
  const { data: auditLogsResponse, loading, error, refetch } = useAuditLogs(
    {
      page: currentPage - 1, // API uses 0-based indexing
      size: pageSize,
    },
    {
      enabled: true,
    }
  );

  // Convert API response to AuditLog array
  const allLogs = useMemo(() => {
    if (!auditLogsResponse?.data?.content) {
      return [];
    }
    return auditLogsResponse.data.content.map(mapApiLogToAuditLog);
  }, [auditLogsResponse]);

  // Server-side pagination info
  const totalElements = auditLogsResponse?.data?.page?.totalElements ?? 0;
  const totalPages = auditLogsResponse?.data?.page?.totalPages ?? 0;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length > 2;

  // Client-side filter (search + action + status) on the current page's data
  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      const matchesSearch = !shouldSearch ||
        log.performedBy.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        log.entity.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        log.entityId.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      const matchesAction = actionFilter === 'ALL' || log.action === actionFilter;
      const matchesStatus = statusFilter === 'ALL' || log.status === statusFilter;

      return matchesSearch && matchesAction && matchesStatus;
    });
  }, [allLogs, shouldSearch, debouncedSearchTerm, actionFilter, statusFilter]);

  // The displayed logs are already the current page from the server
  const paginatedLogs = filteredLogs;

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'UPDATE':
        return <Edit className="w-4 h-4 text-warning" />;
      case 'DELETE':
        return <Trash2 className="w-4 h-4 text-destructive" />;
      case 'LOGIN':
      case 'LOGOUT':
        return <User className="w-4 h-4 text-primary" />;
      default:
        return <FileText className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getActionBadge = (action: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      'CREATE': 'default',
      'UPDATE': 'secondary',
      'DELETE': 'destructive',
      'LOGIN': 'outline',
      'LOGOUT': 'outline',

    };

    return (
      <Badge variant={variants[action] || 'secondary'} className="font-medium">
        {action}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    if (status === 'SUCCESS') {
      return <Badge variant="default" className="bg-success text-success-foreground">SUCCESS</Badge>;
    }
    return <Badge variant="destructive">FAILURE</Badge>;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const [exporting, setExporting] = useState(false);

  const downloadCSV = async () => {
    setExporting(true);
    try {
      // Fetch ALL logs for export (not just the current page)
      const allExportLogs = await fetchAllAuditLogs();
      const mappedLogs = allExportLogs.map(mapApiLogToAuditLog);

      // Define headers
      const headers = ['Action', 'Entity', 'Entity ID', 'Performed By', 'Details', 'IP Address', 'Status', 'Timestamp'];

      // Convert logs to CSV format
      const csvRows = [
        headers.join(','),
        ...mappedLogs.map(log => [
          log.action,
          log.entity,
          log.entityId,
          log.performedBy,
          `"${log.details.replace(/"/g, '""')}"`,
          log.ipAddress,
          log.status,
          formatDate(log.timestamp),
        ].join(','))
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setExporting(false);
    }
  };

  // Auto-focus search input on page load
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Audit Logs</h1>
            <p className="text-muted-foreground mt-1">Track all system activities and changes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          )}
          <Button
            onClick={downloadCSV}
            className="bg-gradient-primary hover:opacity-90"
            disabled={loading || exporting || totalElements === 0}
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <div>
                <p className="font-medium">Error loading audit logs</p>
                <p className="text-sm text-muted-foreground">{error.message}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="mt-2"
                >
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  type="search"
                  placeholder="Search by user, entity, details (min 3 chars)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  autoComplete="off"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Action</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Actions</SelectItem>
                  <SelectItem value="CREATE">Create</SelectItem>
                  <SelectItem value="UPDATE">Update</SelectItem>
                  <SelectItem value="DELETE">Delete</SelectItem>
                  <SelectItem value="LOGIN">Login</SelectItem>
                  <SelectItem value="LOGOUT">Logout</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="SUCCESS">Success</SelectItem>
                  <SelectItem value="FAILURE">Failure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Results</label>
              <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                <span className="text-sm text-muted-foreground">
                  {totalElements.toLocaleString()} total {totalElements === 1 ? 'record' : 'records'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Complete history of system actions and changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Entity ID</TableHead>
                  <TableHead>Performed By</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        <p className="text-muted-foreground">Loading audit logs...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedLogs.length > 0 ? (
                  paginatedLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          {getActionBadge(log.action)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{log.entity}</div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{log.entityId}</code>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{log.performedBy}</div>
                          {log.performedByRole && (
                            <div className="text-xs text-muted-foreground">{log.performedByRole}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate text-sm" title={log.details}>
                          {log.details}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{log.ipAddress}</code>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(log.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.timestamp)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-8 h-8" />
                        <p>No audit logs found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalElements > 0 && (
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalElements}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handlePageSizeChange}
              itemsPerPageOptions={[25, 50, 75, 100]}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
